"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";

import { Button } from "@soonlist/ui/button";
import { PUBLIC_LIST_CTA } from "@soonlist/validators";

import { Smartphone, X } from "~/components/icons";
import { capturePublicPageCta } from "~/lib/publicPageAnalytics";

interface OpenInAppBannerProps {
  appUrl: string;
  label?: string;
}

function isInAppBrowser(): boolean {
  if (typeof window === "undefined") return false;

  const ua = navigator.userAgent || navigator.vendor;

  if (ua.includes("Instagram")) return true;

  if (ua.includes("FBAN") || ua.includes("FBAV")) return true;

  if (ua.includes("TikTok") || ua.includes("BytedanceWebview")) return true;

  if (ua.includes("Twitter")) return true;

  if (ua.includes("LinkedInApp")) return true;

  if (
    ua.includes("iPhone") &&
    !ua.includes("Safari") &&
    ua.includes("AppleWebKit")
  ) {
    return true;
  }

  if (
    ua.includes("Android") &&
    (ua.includes("; wv)") ||
      (ua.includes("Version/") && ua.includes("Mobile Safari/")))
  ) {
    return true;
  }

  return false;
}

function isMobile(): boolean {
  if (typeof window === "undefined") return false;

  const ua = navigator.userAgent;
  return (
    /Android|iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function OpenInAppBanner({ appUrl, label }: OpenInAppBannerProps) {
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const pathname = usePathname();
  const posthog = usePostHog();

  useEffect(() => {
    const shouldShow = isInAppBrowser() && isMobile();
    setShowBanner(shouldShow);
  }, []);

  if (!showBanner || dismissed) {
    return null;
  }

  const handleOpenInApp = () => {
    capturePublicPageCta(posthog, {
      cta: PUBLIC_LIST_CTA.OPEN_IN_APP,
      pathname,
      sendBeacon: true,
    });
    window.location.href = appUrl;
  };

  return (
    <div className="mb-4 rounded-xl border border-interactive-2 bg-interactive-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <p className="mb-3 text-sm font-medium text-neutral-1">
            {label ?? "For the best experience, open this in the Soonlist app"}
          </p>
          <Button
            onClick={handleOpenInApp}
            size="sm"
            className="gap-1.5"
            variant="default"
          >
            <Smartphone className="size-4" />
            Open in App
          </Button>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="rounded-full p-1 text-neutral-2 hover:bg-neutral-4 hover:text-neutral-1"
          aria-label="Dismiss"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
